import { useEffect, useMemo } from 'react';

import { useCartContents } from '@zenbusiness/application-commons-ecommerce';
import useAsyncCallback from '@zenbusiness/application-commons-hooks/useAsyncCallback';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';

import CalloutVideo from '#client/components/CalloutVideo';
import ExtraInfoCard from '#client/components/ExtraInfoCard';
import PageHeader from '#client/components/PageHeader';
import ProductOfferView from '#client/components/ProductOfferView';
import useFlowState from '#client/hooks/flow/useFlowState';
import useAddToCart from '#client/hooks/useAddToCart';
import useFlowNavigation from '#client/hooks/useFlowNavigation';
import useProductOfferViewProps from '#client/hooks/useProductOfferViewProps';
import useRemoveFromCart from '#client/hooks/useRemoveFromCart';
import TwoColumnLayout from '#client/layouts/TwoColumnLayout';
import { findProductInCart, selectPurchaseOption } from '#client/utils/product';

const PRODUCT_NAME = 'zb_money_pro';
const MONTHLY_OPTION = 'monthly';
const ANNUAL_OPTION = 'annual';

const MESSAGE_KEY = 'shopping.page.moneyPro';

export default function MoneyProPage() {
  const { get: getProducts } = usePriceList();
  const products = getProducts([PRODUCT_NAME]);
  const { pricing: monthlyPricing } = products.find((p) => p.pricing.interval === MONTHLY_OPTION);
  const { pricing: annualPricing } = products.find((p) => p.pricing.interval === ANNUAL_OPTION);
  const { price: monthPrice, servicePricingUuid: monthlyServicePricingUuid } = monthlyPricing || {};
  const { price: annualPrice, servicePricingUuid: annualServicePricingUuid } = annualPricing || {};
  const { goToNextPage } = useFlowNavigation();
  const { formatMessage } = useIntl();
  const {
    businessInfo: { jurisdictionState }
  } = useFlowState();
  const { addedToCart, addToCart } = useAddToCart();
  const { removedFromCart, removeFromCart } = useRemoveFromCart();
  const { items: cartItems } = useCartContents();

  useEffect(() => {
    if (addedToCart || removedFromCart) {
      goToNextPage();
    }
  }, [addedToCart, goToNextPage, removedFromCart]);

  const purchaseOptions = useMemo(
    () => [
      { id: MONTHLY_OPTION, price: monthPrice },
      { id: ANNUAL_OPTION, price: annualPrice }
    ],
    [monthPrice, annualPrice]
  );

  const selectedOption = useMemo(() => {
    const productInCart = findProductInCart(cartItems, PRODUCT_NAME);
    if (productInCart) {
      return selectPurchaseOption(
        purchaseOptions,
        (option) => option.id === productInCart.interval
      );
    }
    return purchaseOptions[0];
  }, [cartItems, purchaseOptions]);

  const handlePrimaryActionClick = useAsyncCallback(
    async (selectedOptionId) => {
      await addToCart({
        serviceName: PRODUCT_NAME,
        servicePricingUuid:
          selectedOptionId === MONTHLY_OPTION
            ? monthlyServicePricingUuid
            : annualServicePricingUuid,
        jurisdictionState
      });
    },
    [annualServicePricingUuid, addToCart, monthlyServicePricingUuid, jurisdictionState]
  );

  const handleSecondaryActionClick = useAsyncCallback(async () => {
    await removeFromCart([
      { servicePricingUuid: annualServicePricingUuid },
      { servicePricingUuid: monthlyServicePricingUuid }
    ]);
  }, [removeFromCart, annualServicePricingUuid, monthlyServicePricingUuid]);

  const productOfferViewProps = useProductOfferViewProps({
    id: 'moneyPro',
    purchaseOptions,
    selectedOption,
    itemAssets: ['percentrounded', 'trackchangesrounded', 'paymentsrounded'],
    primaryAction: {
      'data-testid': 'money-pro-primary-action',
      onClick: handlePrimaryActionClick
    },
    secondaryAction: {
      'data-testid': 'money-pro-secondary-action',
      onClick: handleSecondaryActionClick
    }
  });

  return (
    <TwoColumnLayout>
      <TwoColumnLayout.SmallColumn>
        <PageHeader
          subtitle={formatMessage({ id: 'shopping.pageHeader.subtitle.moneyPro' })}
          title={formatMessage({ id: 'shopping.pageHeader.title.moneyPro' })}
        />
        <ProductOfferView {...productOfferViewProps} />
      </TwoColumnLayout.SmallColumn>
      <TwoColumnLayout.LargeColumn>
        <Stack spacing={2}>
          <ExtraInfoCard messageKey={MESSAGE_KEY} />
          <CalloutVideo
            clickstreamProps={{
              videoName: 'money_pro_video'
            }}
            thumbnail="/s2/raster/funnel-video-thumbnail-money-pro.png"
            videoId="ltma4fevut"
          />
          <SharedAsset alt="" src="/s2/raster/funnel-money-pro-product-view.png" />
        </Stack>
      </TwoColumnLayout.LargeColumn>
    </TwoColumnLayout>
  );
}
